import React, { useState, useEffect } from 'react';
import api from '../../services/api';

function formatCents(cents) {
  if (cents == null) return '—';
  return `$${(cents / 100).toFixed(2)}`;
}

function formatDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString();
}

function AdminSubscriptions() {
  const [subscriptions, setSubscriptions] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [plans, setPlans] = useState([]);
  const [view, setView] = useState('subscriptions'); // subscriptions | transactions
  const [filterStatus, setFilterStatus] = useState('');
  const [filterPlan, setFilterPlan] = useState('');
  const [grantModal, setGrantModal] = useState(null); // { userId, username }
  const [grantPlanId, setGrantPlanId] = useState('');
  const [grantMonths, setGrantMonths] = useState('');
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterStatus) params.status = filterStatus;
      if (filterPlan) params.planId = filterPlan;
      const [subRes, planRes] = await Promise.all([
        api.get('/admin/subscriptions', { params }),
        api.get('/admin/plans'),
      ]);
      setSubscriptions(subRes.data);
      setPlans(planRes.data);
    } catch (err) {
      alert('Failed to load subscriptions: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  const fetchTransactions = async () => {
    try {
      const res = await api.get('/admin/subscriptions/transactions', { params: { limit: 200 } });
      setTransactions(res.data);
    } catch (err) {
      alert('Failed to load transactions: ' + (err.response?.data?.error || err.message));
    }
  };

  useEffect(() => { fetchData(); }, [filterStatus, filterPlan]);

  const handleGrant = async (e) => {
    e.preventDefault();
    try {
      await api.post('/admin/subscriptions/grant', {
        userId: grantModal.userId,
        planId: parseInt(grantPlanId, 10),
        monthsToGrant: grantMonths ? parseInt(grantMonths, 10) : undefined,
      });
      setGrantModal(null);
      fetchData();
    } catch (err) {
      alert('Failed to grant plan: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleTabChange = (tab) => {
    setView(tab);
    if (tab === 'transactions' && transactions.length === 0) fetchTransactions();
  };

  if (loading) return <div className="loading-spinner" />;

  return (
    <div>
      <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem', borderBottom: '1px solid #eee', paddingBottom: '0.75rem' }}>
        {['subscriptions', 'transactions'].map(tab => (
          <button
            key={tab}
            onClick={() => handleTabChange(tab)}
            style={{
              border: 'none', background: 'none', cursor: 'pointer', fontWeight: view === tab ? 'bold' : 'normal',
              borderBottom: view === tab ? '2px solid var(--color-primary, #4a90d9)' : 'none',
              paddingBottom: '0.25rem', textTransform: 'capitalize',
            }}
          >
            {tab}
          </button>
        ))}
      </div>

      {view === 'subscriptions' && (
        <>
          <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
            <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={{ padding: '0.35rem 0.5rem', borderRadius: '4px', border: '1px solid #ddd' }}>
              <option value="">All statuses</option>
              {['active', 'cancelled', 'grandfathered', 'grace_period', 'past_due'].map(s => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
            <select value={filterPlan} onChange={e => setFilterPlan(e.target.value)} style={{ padding: '0.35rem 0.5rem', borderRadius: '4px', border: '1px solid #ddd' }}>
              <option value="">All plans</option>
              {plans.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
            <button onClick={fetchData} style={{ padding: '0.35rem 0.75rem', border: '1px solid #ddd', borderRadius: '4px', background: 'none', cursor: 'pointer' }}>Refresh</button>
          </div>

          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875em' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #eee' }}>
                  {['User', 'Plan', 'Status', 'Billing', 'Period End', 'Grandfathered Until', 'Family Group', 'Actions'].map(h => (
                    <th key={h} style={{ padding: '0.5rem 0.75rem', textAlign: 'left', fontWeight: 'bold' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {subscriptions.map(sub => (
                  <tr key={sub.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <td style={{ padding: '0.5rem 0.75rem' }}>
                      <div>{sub.username}</div>
                      <div style={{ opacity: 0.6, fontSize: '0.85em' }}>{sub.email || sub.phone}</div>
                    </td>
                    <td style={{ padding: '0.5rem 0.75rem' }}>{sub.plan_name}</td>
                    <td style={{ padding: '0.5rem 0.75rem' }}>
                      <span style={{
                        padding: '2px 8px', borderRadius: '10px', fontSize: '0.8em',
                        background: sub.status === 'active' ? '#d1fae5' : sub.status === 'grandfathered' ? '#dbeafe' : '#fef3c7',
                        color: sub.status === 'active' ? '#065f46' : sub.status === 'grandfathered' ? '#1e40af' : '#92400e',
                      }}>
                        {sub.status}
                      </span>
                    </td>
                    <td style={{ padding: '0.5rem 0.75rem' }}>{sub.billing_period || '—'}</td>
                    <td style={{ padding: '0.5rem 0.75rem' }}>{formatDate(sub.current_period_end)}</td>
                    <td style={{ padding: '0.5rem 0.75rem' }}>{formatDate(sub.grandfathered_until)}</td>
                    <td style={{ padding: '0.5rem 0.75rem' }}>{sub.family_group_name || '—'}</td>
                    <td style={{ padding: '0.5rem 0.75rem' }}>
                      <button
                        onClick={() => { setGrantModal({ userId: sub.user_id, username: sub.username }); setGrantPlanId(String(sub.plan_id)); setGrantMonths(''); }}
                        style={{ border: '1px solid #ddd', borderRadius: '4px', padding: '0.25rem 0.5rem', background: 'none', cursor: 'pointer', fontSize: '0.8em' }}
                      >
                        Grant Plan
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {view === 'transactions' && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875em' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #eee' }}>
                {['Date', 'User', 'Amount', 'Method', 'Status', 'Provider ID', 'Error'].map(h => (
                  <th key={h} style={{ padding: '0.5rem 0.75rem', textAlign: 'left' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {transactions.map(tx => (
                <tr key={tx.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                  <td style={{ padding: '0.5rem 0.75rem' }}>{formatDate(tx.created_at)}</td>
                  <td style={{ padding: '0.5rem 0.75rem' }}>{tx.username}</td>
                  <td style={{ padding: '0.5rem 0.75rem' }}>{formatCents(tx.amount_cents)}</td>
                  <td style={{ padding: '0.5rem 0.75rem' }}>{tx.payment_method || '—'}</td>
                  <td style={{ padding: '0.5rem 0.75rem' }}>
                    <span style={{ color: tx.status === 'succeeded' ? 'green' : tx.status === 'failed' ? 'red' : undefined }}>
                      {tx.status}
                    </span>
                  </td>
                  <td style={{ padding: '0.5rem 0.75rem', fontSize: '0.8em', opacity: 0.7 }}>{tx.provider_transaction_id || '—'}</td>
                  <td style={{ padding: '0.5rem 0.75rem', color: 'red', fontSize: '0.8em' }}>{tx.error_message || ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Grant Plan Modal */}
      {grantModal && (
        <div className="modal-overlay" onClick={() => setGrantModal(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: '400px' }}>
            <div className="modal-header">
              <h3>Grant Plan to {grantModal.username}</h3>
              <button onClick={() => setGrantModal(null)}>&#10005;</button>
            </div>
            <form onSubmit={handleGrant} style={{ padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.875em' }}>Plan</label>
                <select value={grantPlanId} onChange={e => setGrantPlanId(e.target.value)} required style={{ width: '100%', padding: '0.4rem', borderRadius: '4px', border: '1px solid #ddd' }}>
                  <option value="">Select a plan…</option>
                  {plans.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.875em' }}>Grant for (months, blank = indefinite)</label>
                <input type="number" value={grantMonths} onChange={e => setGrantMonths(e.target.value)} placeholder="e.g. 12" min="1" style={{ width: '100%', padding: '0.4rem', borderRadius: '4px', border: '1px solid #ddd', boxSizing: 'border-box' }} />
              </div>
              <p style={{ fontSize: '0.8em', opacity: 0.7 }}>No payment will be charged. This is an admin override.</p>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button type="button" onClick={() => setGrantModal(null)} style={{ flex: 1, padding: '0.4rem', border: '1px solid #ddd', borderRadius: '4px', background: 'none', cursor: 'pointer' }}>Cancel</button>
                <button type="submit" className="btn-save" style={{ flex: 1 }}>Grant</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminSubscriptions;
